import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { nestedTableStyling, defaultDocumentConfig } from './GenericSettlementsFunctions.js'
import { tonStringFromPounds, numberWithCommas } from '@/utils/NumericMutations'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { generateSvgForPdf } from '@/images/AngelSvgLibrary'

const mdiCrosshairsGpsPath = 'M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z'
const mdiAccountArrowRightPath = 'M18 16H14V18H18V20L21 17L18 14V16M11 4C8.8 4 7 5.8 7 8S8.8 12 11 12 15 10.2 15 8 13.2 4 11 4M11 14C6.6 14 3 15.8 3 18V20H12.5C12.2 19.2 12 18.4 12 17.5C12 16.3 12.3 15.2 12.9 14.1C12.3 14.1 11.7 14 11 14'

PDFMake.vfs = pdfFonts.pdfMake.vfs

export const generatePdf = ({
  tickets,
  deck,
  location,
  entity,
  headers,
  products
}) => {
  const docDefinition = {
    ...defaultDocumentConfig,
    header: getHeader(entity, location, deck),
    footer: getFooter,
    content: content(tickets, headers, products),
    pageOrientation: 'landscape',
    style: nestedTableStyling
  }

  PDFMake.createPdf(docDefinition).download(`${entity}_${location}_${deck}.pdf`)
}

const getHeader = (entity, location, deck) => ({
  fontSize: 15,
  bold: true,
  marginTop: 10,
  marginLeft: 35,
  marginRight: 35,
  table: {
    widths: ['auto', '*', 'auto'],
    body: [
      [
        {
          table: {
            body: [
              [{ text: entity, alignment: 'left', layout: 'noBorders' }],
              [{ text: location, alignment: 'left', layout: 'noBorders', fontSize: 12 }]
            ]
          },
          layout: 'noBorders'
        },
        { text: '' },
        {
          table: {
            body: [
              [{ text: `Deck: ${deck}`, alignment: 'left', layout: 'noBorders', fontSize: 12 }]
            ]
          },
          layout: 'noBorders',
          alignment: 'right'
        }
      ]
    ]
  },
  layout: 'noBorders'
})

function getFooter (page, pages) {
  return {
    marginRight: 50,
    marginLeft: 50,
    columns: [
      {
        width: '*',
        text: `${page}/${pages}`,
        fontSize: 8,
        bold: true
      },
      {
        width: '*',
        text: `${utcToLocalDate(new Date().toISOString(), 'L - LT')}`,
        alignment: 'right',
        fontSize: 8,
        bold: true
      }
    ]
  }
}

const content = (tickets, headers, products) => {
  const headerRow = headers.map(h => ({ text: h.text, fontSize: 8, alignment: h.align ? h.align : 'left', bold: true }))

  const valueOf = (ticket, header) => {
    switch (header.value) {
      case 'netWeight':
        const netWeight = ticket.inWeight - ticket.outWeight - ticket.defectWeight
        if (ticket.inWeight <= 0) {
          return 'N/A'
        } else {
          return tonStringFromPounds(netWeight)
        }
      case 'mbf':
        return numberWithCommas(Math.ceil((ticket.inWeight - ticket.outWeight - ticket.defectWeight) * products[ticket.product].conversionRateW2V))
      case 'extTicketNumber1' || 'extTicketNumber1':
        if (ticket[header.value] === '') {
          return '-'
        } else {
          return ticket[header.value]
        }
      case 'source': {
        return ticket.tract ?? ticket.fromAccount
      }
      default:
        return header.format ? header.format(ticket[header.value]) : ticket[header.value]
    }
  }

  const bodyRows = tickets
    .map((t) => headers
      .map(h => ({
        columns: [
          iconShouldBeDisplayed(t, h)
            ? {
              svg: generateSvgForPdf({ path: t.tract ? mdiCrosshairsGpsPath : mdiAccountArrowRightPath }),
              width: 10
            }
            : undefined,
          {
            text: valueOf(t, h),
            fontSize: 8,
            alignment: h.align,
            marginLeft: iconShouldBeDisplayed(t, h) ? 2 : 0
          }
        ]
          .filter(i => i)
      }))
    )

  return [
    {
      marginBottom: 24,
      layout: tableLayout(),
      table: {
        alignment: 'right',
        headerRows: 1,
        dontBreakRows: true,
        body: [
          [
            { text: 'Totals', fontSize: 8, bold: true },
            { text: '', fontSize: 8 }
          ],
          [
            { text: 'Loads', fontSize: 8 },
            { text: `${tickets.length}`, alignment: 'right', fontSize: 8 }
          ],
          [
            { text: 'Net Tons', fontSize: 8 },
            { text: `${tonStringFromPounds(tickets.map((t) => t.inWeight - t.outWeight - t.defectWeight).reduce((a, b) => a + b, 0))}`, alignment: 'right', fontSize: 8 }
          ],
          [
            { text: 'MBF', fontSize: 8 },
            { text: `${numberWithCommas(tickets.map((t) => (t.inWeight - t.outWeight - t.defectWeight) * products[t.product].conversionRateW2V).reduce((a, b) => a + b, 0))}`, alignment: 'right', fontSize: 8 }
          ]
        ]
      }
    },
    {
      marginBottom: 24,
      layout: tableLayout(),
      table: {
        widths: headers.map(h => h.width),
        headerRows: 1,
        body: [
          headerRow,
          ...bodyRows
        ]
      }
    }
  ]
}

function iconShouldBeDisplayed (ticket, header) {
  if (header.value === 'source') {
    return ticket.tract || ticket.fromAccount
  }
  return false
}

const tableLayout = () => ({
  fillColor: function (rowIndex) {
    return (rowIndex % 2 === 0) ? 'white' : 'lightgrey'
  },
  vLineWidth: function (i) {
    return 0
  },
  hLineWidth: function (i) {
    if (i === 1) {
      return 1
    } else {
      return 0
    }
  }
})
